import React, { FC } from "react";
import { Box, FormControl, Radio, RadioGroup, Text } from "@primer/react";
import { Banner } from "@primer/react/experimental";
import { BaseDialog } from "@getregistered/greg-editors/dist/lib/primer-ui/BaseDialog/BaseDialog";
import { StatusBreakdown } from "./RegistrationStatusContent";

interface RestoreConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectionCount: number;
  preflightCounts?: Record<string, number>;
  sendEmail: boolean;
  onSendEmailChange: (value: boolean) => void;
}

// Keep in sync with BulkActions::RegistrationRestoreExecutor: only cancelled
// registrations are restorable; a cancelled row is still skipped server-side if
// the attendee has re-registered or its ticket type is now full, so the copy
// promises "up to" rather than exactly.
const describeRestoreRow = (status: string, isAlreadyTarget: boolean) =>
  isAlreadyTarget
    ? "These will be restored (skipped if the attendee re-registered or the ticket type is full)"
    : "Only cancelled registrations can be restored — these will be skipped";

export const RestoreConfirmationModal: FC<RestoreConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectionCount,
  preflightCounts,
  sendEmail,
  onSendEmailChange,
}) => {
  // Until preflight resolves, assume the whole selection is restorable so the
  // button isn't disabled on a slow network.
  const restorableCount = preflightCounts ? (preflightCounts.cancelled ?? 0) : selectionCount;
  const nothingRestorable = restorableCount === 0;
  const registrationLabel = restorableCount === 1 ? "registration" : "registrations";
  const skippedStatuses = preflightCounts
    ? Object.keys(preflightCounts).filter((status) => status !== "cancelled")
    : undefined;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Restore Registrations"
      position="center"
      width="600px"
      leftButtonText="Go back"
      rightButtonText={`Restore ${restorableCount} ${registrationLabel}`}
      rightButtonVariant="primary"
      rightButtonDisabled={nothingRestorable}
      onLeftButtonClick={onClose}
      onRightButtonClick={nothingRestorable ? undefined : onConfirm}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {nothingRestorable ? (
          <Banner
            variant="warning"
            title={`None of the selected ${selectionCount === 1 ? "registration" : "registrations"} can be restored. Only cancelled registrations can be restored.`}
          />
        ) : (
          <Text as="div" fontSize={1}>
            Restoring{" "}
            <Text as="span" fontWeight="bold">{restorableCount} cancelled {registrationLabel}</Text>
            {" "}back to active.
          </Text>
        )}

        {preflightCounts && (
          <StatusBreakdown
            counts={preflightCounts}
            targetStatus="cancelled"
            describeRow={describeRestoreRow}
            skippedStatuses={skippedStatuses}
          />
        )}

        {!nothingRestorable && (
          <RadioGroup name="restore-email">
            <RadioGroup.Label>Confirmation email</RadioGroup.Label>
            <FormControl>
              <Radio value="send" checked={sendEmail} onChange={() => onSendEmailChange(true)} />
              <FormControl.Label>Send confirmation email</FormControl.Label>
              <FormControl.Caption>
                Only registrations restored to confirmed are emailed; ones restored to started are not.
              </FormControl.Caption>
            </FormControl>
            <FormControl>
              <Radio value="none" checked={!sendEmail} onChange={() => onSendEmailChange(false)} />
              <FormControl.Label>Don&apos;t send email</FormControl.Label>
            </FormControl>
          </RadioGroup>
        )}

        <Text as="div" fontSize={1}>
          Only cancelled registrations are restored — other statuses are skipped. A restored
          registration returns to confirmed (or started, if it was never confirmed); rows are
          skipped if the attendee has since re-registered or the ticket type is now full.
        </Text>
      </Box>
    </BaseDialog>
  );
};
